import log from '@/lib/logger';
import type { AppDispatch } from '@/store';
import type { ChargingProfile } from '@/types/swagger';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import { http } from 'config';
import { showSnackbarMessage } from 'features/Common';
import _ from 'lodash';
import Nprogress from 'nprogress';
import { getNewImmutableState } from 'util/getNewImmutableState';

// ----------------------------------- Actions ---------------------------------
// Action Types
export const LOAD_CHARGING_PROFILES = 'charging_profiles/load_charging_profiles';
export const LOAD_GRID_CONNECTION_CHARGING_PROFILE =
  'charging_profiles/load_grid_connection_charging_profile';
export const ADD_CHARGING_PROFILE = 'charging_profiles/add_charging_profile';
export const UPDATE_CHARGING_PROFILE = 'charging_profiles/update_charging_profile';
export const UPDATE_CHARGING_PROFILE_START = 'charging_profiles/update_charging_profile_start';
export const UPDATE_CHARGING_PROFILE_END = 'charging_profiles/update_charging_profile_end';
export const UPDATE_CHARGING_PROFILE_SYNC_STATUSES =
  'charging_profiles/update_charging_profile_sync_statuses';
export const UPDATE_CHARGING_PROFILE_RESYNC_STATUSES =
  'charging_profiles/update_charging_profile_resync_statuses';

// Action Creators
export const loadChargingProfiles = (chargingProfiles: Record<number, ChargingProfile>) => ({
  type: LOAD_CHARGING_PROFILES,
  payload: chargingProfiles,
});
export const loadGridConnectionChargingProfile = (
  gridConnectionId: number,
  chargingProfile: ChargingProfile
) => ({
  type: LOAD_GRID_CONNECTION_CHARGING_PROFILE,
  meta: gridConnectionId,
  payload: chargingProfile,
});
export const addChargingProfile = (chargingProfile: Record<number, ChargingProfile>) => ({
  type: ADD_CHARGING_PROFILE,
  payload: chargingProfile,
});
export const updateChargingProfile = (chargingProfile: Record<number, ChargingProfile>) => ({
  type: UPDATE_CHARGING_PROFILE,
  payload: chargingProfile,
});
export const updateChargingProfileStart = () => ({
  type: UPDATE_CHARGING_PROFILE_START,
});
export const updateChargingProfileEnd = () => ({
  type: UPDATE_CHARGING_PROFILE_END,
});

// ----------------------------------- Reducer ---------------------------------

interface ChargingProfilesState {
  byId: Record<number, any>;
  byGridConnectionId: Record<number, any>;
  allIds: number[];
  syncStatusesById: Record<number, any>;
  resyncStatusesById: Record<number, any>;
  fetching: boolean;
  updating: boolean;
}

// Init State
const INIT_STATE: ChargingProfilesState = {
  byId: {},
  byGridConnectionId: {},
  allIds: [],
  syncStatusesById: {},
  resyncStatusesById: {},
  fetching: false,
  updating: false,
};

const chargingProfileReducer = (state = INIT_STATE, action: UnknownAction) => {
  const { type, meta, payload } = action as PayloadAction & { meta: number };
  switch (type) {
    case LOAD_CHARGING_PROFILES: {
      return getNewImmutableState(state, payload);
    }
    case LOAD_GRID_CONNECTION_CHARGING_PROFILE: {
      const newState = _.cloneDeep(state);
      newState.byGridConnectionId[meta] = payload;
      return newState;
    }
    case ADD_CHARGING_PROFILE: {
      return getNewImmutableState(state, payload);
    }
    case UPDATE_CHARGING_PROFILE_START: {
      return { ...state, updating: true };
    }
    case UPDATE_CHARGING_PROFILE: {
      return getNewImmutableState(state, payload);
    }
    case UPDATE_CHARGING_PROFILE_END: {
      return { ...state, updating: false };
    }
    case UPDATE_CHARGING_PROFILE_SYNC_STATUSES: {
      return getNewImmutableState(state, payload);
    }
    case UPDATE_CHARGING_PROFILE_RESYNC_STATUSES: {
      return getNewImmutableState(state, payload);
    }
    default:
      return state;
  }
};

// Reducer
export default chargingProfileReducer;

// ----------------------------------- Side Effects ----------------------------
// Thunks
export const patchChargingProfile =
  (chargingProfile: Partial<ChargingProfile>) => async (dispatch: AppDispatch) => {
    if (!chargingProfile?.id) return;
    Nprogress.start();
    const apiChargingProfile = `chargepoints/charging_schedules/${chargingProfile.id}/`;
    dispatch(updateChargingProfileStart());
    try {
      const response = await http.patch(apiChargingProfile, chargingProfile.charging_schedule);
      const updatedChargingProfile = { ...chargingProfile };
      updatedChargingProfile.charging_schedule = response.data;
      if (!updatedChargingProfile.charging_schedule) return;
      dispatch(
        updateChargingProfile({
          [chargingProfile.id]: {
            ...updatedChargingProfile,
            sync_status: null,
          },
        } as Record<number, ChargingProfile>)
      );

      dispatch(
        showSnackbarMessage(
          (updatedChargingProfile.charging_schedule.charging_schedule_periods?.length
            ? 'Perfil guardado.'
            : 'Perfil eliminado.') + ' Esperando respuesta del cargador...'
        )
      );
      dispatch(updateChargingProfileEnd());
      Nprogress.done();
      return response.data;
    } catch (error) {
      dispatch(showSnackbarMessage('No se ha podido guardar el perfil.'));
      dispatch(updateChargingProfileEnd());
      log.error(error);
      Nprogress.done();
      return error;
    }
  };

export const fetchGridConnectionChargingProfile =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    Nprogress.start();
    const apiGridConnections = `company/grid_connections/${gridConnectionId}/composite_schedule/?page_size=999`;
    http
      .get(apiGridConnections)
      .then((response) => {
        dispatch(loadGridConnectionChargingProfile(gridConnectionId, response.data));
        Nprogress.done();
        return response.data.results;
      })
      .catch((error) => {
        log.error(error);
        Nprogress.done();
        return error;
      });
  };
